import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, AlertTriangle, Loader2 } from "lucide-react";
import { CONTACTS, SERVICES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  phone: "",
  vehicle: "",
  service_type: SERVICES[0].title,
  is_emergency: false,
  location: "",
  message: "",
};

const inputCls =
  "w-full bg-carbon border border-steel px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-hazard transition-colors duration-300";

export default function RequestForm() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone number are required.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/requests`, form);
      toast.success("Service request received. Omar or Ray will call you back shortly.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Could not send the request. Please call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-carbon" data-testid="service-request-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-widest text-hazard font-semibold mb-3">/ 05 — Dispatch Form</p>
          <h2 className="font-heading uppercase font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-[0.95]">
            Request Service <span className="text-racing italic">or a Tow</span>
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed max-w-md">
            Tell us what your rig needs and where it is. We review every request and call you back
            with a plan and an honest estimate.
          </p>
          <div className="mt-8 border border-racing/50 bg-racing/10 p-5 flex gap-4 items-start" data-testid="emergency-alert-box">
            <AlertTriangle className="w-6 h-6 text-racing shrink-0 mt-0.5" />
            <p className="text-sm text-slate-200 leading-relaxed">
              <span className="font-heading font-bold uppercase text-racing">Emergency roadside?</span>{" "}
              For immediate 24/7 towing, call directly:{" "}
              <a href={CONTACTS.omar.tel} className="text-hazard font-semibold hover:underline">Omar {CONTACTS.omar.phone}</a>{" "}
              or{" "}
              <a href={CONTACTS.ray.tel} className="text-hazard font-semibold hover:underline">Ray {CONTACTS.ray.phone}</a>
            </p>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-7 border border-steel bg-surface p-6 sm:p-10"
          data-testid="service-request-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Full Name *</label>
              <input data-testid="service-request-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="John Trucker" required />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Phone Number *</label>
              <input data-testid="service-request-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(909) 555-0100" required />
            </div>
            <div className="sm:col-span-2">
              <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Vehicle Make / Model / Year</label>
              <input data-testid="service-request-vehicle-input" className={inputCls} value={form.vehicle} onChange={set("vehicle")} placeholder="e.g. 2021 Freightliner Cascadia" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Service Type Needed</label>
              <select data-testid="service-request-service-type-select" className={inputCls} value={form.service_type} onChange={set("service_type")}>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title}>{s.title}</option>
                ))}
                <option value="Other / Not Sure">Other / Not Sure</option>
              </select>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Current Location / Drop-off Date</label>
              <input data-testid="service-request-location-input" className={inputCls} value={form.location} onChange={set("location")} placeholder="I-10 near Cedar Ave, or shop drop-off" />
            </div>
            <div className="sm:col-span-2">
              <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Problem Description</label>
              <textarea data-testid="service-request-message-input" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Describe symptoms, sounds, warning lights, load status..." />
            </div>
            <label className="sm:col-span-2 flex items-center gap-3 cursor-pointer group" data-testid="service-request-emergency-checkbox">
              <input type="checkbox" checked={form.is_emergency} onChange={set("is_emergency")} className="w-5 h-5 accent-[#FF2A2A]" />
              <span className="text-sm text-slate-300 group-hover:text-white transition-colors">
                This is an <span className="text-racing font-semibold">emergency roadside / towing</span> request
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={sending}
            data-testid="service-request-submit-button"
            className="mt-8 w-full flex items-center justify-center gap-3 px-8 py-4 bg-hazard text-carbon font-heading font-extrabold uppercase text-lg tracking-wide hover:bg-white transition-colors duration-300 disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            {sending ? "Dispatching..." : "Submit Service Request"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
